// Per-dog skill progress tracker. Skills come from two places:
//   • The saved verbal cues (auto-suggested when adding — they don't get
//     imported automatically; you decide which cues become tracked skills).
//   • Custom skills you add yourself, e.g. "loose-leash walking".
//
// Mastery is a 3-step pill: 🔵 learning → 🟡 practicing → 🟢 mastered.
// Clicking the pill cycles to the next stage.
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionTitle } from "@/components/app/Bits";
import { Plus, Trash2, Target } from "lucide-react";

const MASTERY = [
  { value: "learning",   label: "Learning",   emoji: "🔵", cls: "bg-primary/10 text-primary hover:bg-primary/15" },
  { value: "practicing", label: "Practicing", emoji: "🟡", cls: "bg-accent/40 text-accent-foreground hover:bg-accent/60" },
  { value: "mastered",   label: "Mastered",   emoji: "🟢", cls: "bg-green-100 text-green-800 hover:bg-green-200" },
];
const MASTERY_META = Object.fromEntries(MASTERY.map((m) => [m.value, m]));
const NEXT_MASTERY = { learning: "practicing", practicing: "mastered", mastered: "learning" };

export default function SkillsSection({ dogId }) {
  const [items, setItems] = useState([]);
  const [cues, setCues] = useState([]);
  const [form, setForm] = useState({ name: "", mastery: "learning", source: "custom" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!dogId) return;
    try {
      const [skillsRes, cuesRes] = await Promise.all([
        api.get(`/dogs/${dogId}/skills`),
        api.get(`/dogs/${dogId}/verbal-cues`),
      ]);
      setItems(skillsRes.data.items || []);
      setCues(cuesRes.data.items || []);
    } catch {
      setItems([]);
      setCues([]);
    }
  }, [dogId]);

  useEffect(() => { load(); }, [load]);

  // Which cues aren't already tracked — offered as one-tap "Track this cue" chips.
  const trackedNames = useMemo(() => new Set(items.map((s) => s.name.toLowerCase())), [items]);
  const untrackedCues = useMemo(() => cues.filter((c) => !trackedNames.has(c.label.toLowerCase())), [cues, trackedNames]);

  const submit = async (e, override = null) => {
    if (e) e.preventDefault();
    const payload = override || { name: form.name.trim(), mastery: form.mastery, source: form.source };
    if (!payload.name) return;
    setSaving(true);
    try {
      await api.post(`/dogs/${dogId}/skills`, payload);
      if (!override) setForm({ name: "", mastery: "learning", source: "custom" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not add skill");
    } finally { setSaving(false); }
  };

  const cycleMastery = async (skill) => {
    const nextMastery = NEXT_MASTERY[skill.mastery] || "learning";
    // Optimistic update
    setItems((prev) => prev.map((s) => (s.id === skill.id ? { ...s, mastery: nextMastery } : s)));
    try {
      await api.put(`/dogs/${dogId}/skills/${skill.id}`, { mastery: nextMastery });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not update");
      await load();
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/dogs/${dogId}/skills/${id}`);
      await load();
    } catch { toast.error("Could not delete"); }
  };

  const grouped = useMemo(() => {
    const g = { learning: [], practicing: [], mastered: [] };
    for (const s of items) (g[s.mastery] || g.learning).push(s);
    return g;
  }, [items]);

  return (
    <div className="space-y-4" data-testid="skills-section">
      <SectionTitle overline="Progress" title="Skill tracker" />
      <p className="text-sm text-muted-foreground max-w-2xl -mt-2">
        Track what you&apos;re working on. Click a mastery pill to cycle{" "}
        <span className="inline-block px-1.5 py-0.5 rounded bg-primary/10 text-primary text-xs mx-0.5">🔵 learning</span>
        →{" "}
        <span className="inline-block px-1.5 py-0.5 rounded bg-accent/40 text-accent-foreground text-xs mx-0.5">🟡 practicing</span>
        →{" "}
        <span className="inline-block px-1.5 py-0.5 rounded bg-green-100 text-green-800 text-xs mx-0.5">🟢 mastered</span>.
      </p>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-5 sm:p-6 space-y-4">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="skill-form">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Skill name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder='e.g. "Loose-leash walking", "Recall from distraction"'
                className="mt-1.5"
                maxLength={80}
                data-testid="skill-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Starting at</Label>
              <select
                value={form.mastery}
                onChange={(e) => setForm({ ...form, mastery: e.target.value })}
                className="mt-1.5 w-full border border-input rounded-md bg-card px-3 py-2 text-sm"
                data-testid="skill-mastery"
              >
                {MASTERY.map((m) => <option key={m.value} value={m.value}>{m.emoji} {m.label}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <Button
                type="submit"
                disabled={saving || !form.name.trim()}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="skill-add"
              >
                <Plus size={14} className="mr-1.5" /> Add skill
              </Button>
            </div>
          </form>

          {untrackedCues.length > 0 ? (
            <div className="pt-3 border-t border-border" data-testid="skill-cue-suggestions">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Track a cue you already saved
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {untrackedCues.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => submit(null, { name: c.label, mastery: "learning", source: "cue" })}
                      className="inline-flex items-center gap-1 text-xs bg-secondary/60 hover:bg-secondary text-foreground/85 rounded-full pl-2.5 pr-3 py-1"
                      data-testid={`skill-add-from-cue-${c.id}`}
                    >
                      <Plus size={11} /> {c.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {items.length === 0 ? (
        <Card className="border-dashed border-border shadow-none rounded-lg">
          <CardContent className="p-6 text-sm text-muted-foreground text-center">
            No skills tracked yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="skills-list">
          {MASTERY.map((m) => (
            <div key={m.value} className="space-y-2" data-testid={`skills-column-${m.value}`}>
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                {m.emoji} {m.label} ({grouped[m.value].length})
              </p>
              {grouped[m.value].length === 0 ? (
                <p className="text-xs text-muted-foreground italic">—</p>
              ) : (
                <ul className="space-y-1.5">
                  {grouped[m.value].map((s) => {
                    const cur = MASTERY_META[s.mastery] || MASTERY_META.learning;
                    return (
                      <li
                        key={s.id}
                        className="flex items-center gap-2 bg-card border border-border rounded-md p-2.5 group"
                        data-testid={`skill-item-${s.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => cycleMastery(s)}
                          className={`text-xs rounded-full px-2 py-0.5 transition-colors ${cur.cls}`}
                          title="Click to cycle mastery"
                          data-testid={`skill-mastery-pill-${s.id}`}
                        >
                          {cur.emoji}
                        </button>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">
                            {s.name}
                            {s.source === "cue" ? (
                              <span className="ml-1.5 text-[9px] uppercase tracking-wider text-muted-foreground bg-secondary/60 rounded px-1 py-0.5">cue</span>
                            ) : null}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => remove(s.id)}
                          className="text-muted-foreground hover:text-destructive p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove"
                          data-testid={`skill-remove-${s.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
